import { FEATURE_CONTROLLED_COMPANY_ID, FEATURE_CONTROLLED_LOCATION_ID } from './constants'
import moment, { Moment } from 'moment-timezone'

import { MessageType } from '../models/message'
import { automationRouteNames } from '@/routes/constants/location'
import camelCase from 'lodash-es/camelCase'
import firebase from 'firebase/compat/app'
import mapKeys from 'lodash-es/mapKeys'
import { prospectingRoutes } from '@/routes/constants/agency/prospecting'
import snakeCase from 'lodash-es/snakeCase'
import store from '../store'
import { users } from '../store/users'
import { i18n } from '@/localization'

export class Utils {
  /**
   * Checks if the current company ID matches the special company ID
   * This is used to apply special behavior for a specific company
   * @returns boolean indicating whether the current company is the special company
   */
  public static isSpecialCompany(): boolean {
    const company = store.state?.company?.company;
    const location = store.state?.locations?.currentLocation;
    
    // For FEATURE_CONTROLLED_COMPANY_ID, we show the special behavior
    // EXCEPT for FEATURE_CONTROLLED_LOCATION_ID which uses the normal flow
    if (company && company.id === FEATURE_CONTROLLED_COMPANY_ID) {
      // If we're in the special location, return false to use normal flow
      if (location && location.id === FEATURE_CONTROLLED_LOCATION_ID) {
        return false;
      }
      // For all other locations in this company, return true for special flow
      return true;
    }
    
    // For all other companies, return false
    return false;
  }

  public static safeMoment(item: any) {
    if (typeof item === 'number') return moment(item)
    if (typeof item === 'string') return moment(item)
    if (item && item._seconds)
      return moment(new firebase.firestore.Timestamp(item._seconds, item._nanoseconds).toMillis())
    return item
  }

  public static safeReplaceDoubleQuotes(item: any) {
    return item ? `${item}`.replace(/\"/g, '""') : ''
  }

  public static getUserName(id: string, returnFullName = true) {
    if (Utils.isEmptyStr(id)) return ''
    if (!users || !users.state || !users.state.users) return id
    const x = users.state.users.find(x => x.id === id)
    return (x && `${x.first_name} ${returnFullName ? x.last_name : ''}`) || id // return back whatever id was passed if we can't find the user from Vuex for location
  }

  public static tzTime(m: Moment, timeZone: string): moment.Moment {
    if (!m) return null
    return !Utils.isEmptyStr(timeZone) ? m.clone().tz(timeZone) : m.clone().utc()
  }

  public static getDateParts(m: Moment, timeZone: string): string[] {
    const parts = ['', '', '']
    try {
      if (!m) return parts
      const parseDate = !Utils.isEmptyStr(timeZone) ? m.clone().tz(timeZone) : m.clone().utc()
      parts[0] = parseDate.format('MMM DD YYYY')
      parts[1] = parseDate.format('hh:mm A')
      parts[2] = parseDate.format('z')
    } catch (err) {
      console.log(err)
    }
    return parts
  }

  public static relativeTime(m: Moment, suffix = 'ago'): string {
    if (!m || !m.isValid()) return ''
    let prefix = ''
    let diff = moment().diff(m)
    if (diff < 0) {
      diff = m.diff(moment())
      prefix = 'after '
      suffix = ''
    }

    const ts = moment.duration(diff)
    let d = Math.floor(ts.asYears())
    if (d > 0) return `${prefix}${d} year${d > 1 ? 's' : ''} ${suffix}`.trim()
    d = Math.floor(ts.asMonths())
    if (d > 0) return `${prefix}${d} month${d > 1 ? 's' : ''} ${suffix}`.trim()
    d = Math.floor(ts.asWeeks())
    if (d > 0) return `${prefix}${d} week${d > 1 ? 's' : ''} ${suffix}`.trim()
    d = Math.floor(ts.asDays())
    if (d > 0) return `${prefix}${d} day${d > 1 ? 's' : ''} ${suffix}`.trim()
    d = Math.floor(ts.asHours())
    if (d > 0) return `${prefix}${d} hour${d > 1 ? 's' : ''} ${suffix}`.trim()
    d = Math.floor(ts.asMinutes())
    if (d > 0) return `${prefix}${d} minute${d > 1 ? 's' : ''} ${suffix}`.trim()
    return 'right now'
  }

  public static isCall(messageType: MessageType): boolean {
    return (
      messageType === MessageType.TYPE_CALL ||
      messageType === MessageType.TYPE_CAMPAIGN_CALL ||
      messageType === MessageType.TYPE_CAMPAIGN_MANUAL_CALL
    )
  }

  public static isEmail(messageType: MessageType): boolean {
    return (
      messageType === MessageType.TYPE_EMAIL ||
      messageType === MessageType.TYPE_CAMPAIGN_EMAIL ||
      messageType === MessageType.TYPE_CUSTOM_EMAIL
    )
  }
  public static isActivity(messageType: MessageType): boolean {
    return (
      messageType === MessageType.TYPE_NO_SHOW ||
      messageType === MessageType.TYPE_ACTIVITY_APPOINTMENT ||
      messageType === MessageType.TYPE_ACTIVITY_CONTACT ||
      messageType === MessageType.TYPE_ACTIVITY_INVOICE ||
      messageType === MessageType.TYPE_ACTIVITY_OPPORTUNITY ||
      messageType === MessageType.TYPE_ACTIVITY_PAYMENT ||
      messageType === MessageType.TYPE_ACTIVITY_EMPLOYEE_ACTION_LOG
    )
  }

  public static isWhatsApp(messageType: MessageType): boolean {
    return messageType === MessageType.TYPE_WHATSAPP
  }

  public static serverReady(item: string) {
    if (Utils.isEmptyStr(item) === false) {
      return item
        .trim()
        .toLowerCase()
        .replace(/\s{1,}/g, '_')
    } else return ''
  }

  public static isEmptyStr(item: string): boolean {
    if (typeof item !== 'string' || item.trim() === '') return true
    else return false
  }

  public static safeLC(item: string): string {
    if (Utils.isEmptyStr(item) === false) return item.trim().toLowerCase()
    return ''
  }

  public static cropDisplay(item: string, len: number) {
    if (Utils.isEmptyStr(item) || item.length <= len) return item
    return `${item.substr(0, len)}..`.trim()
  }

  public static capitalize(item: string) {
    if (typeof item !== 'string' || (item = item.trim()).length <= 0) return item
    return `${item.charAt(0).toUpperCase()}${item.length > 0 ? item.slice(1) : ''}`
  }

  public static isImage = (type: string) => {
    return type.indexOf('image/') === 0
  }

  public static isSupportedMMSType = (filetype: string) => {
    // Check https://www.twilio.com/docs/sms/accepted-mime-types
    // For Testing https://file-examples.com/index.php/sample-audio-files/sample-wav-download/
    return Utils.supportedMMSTypes.indexOf(filetype) >= 0
  }
  public static isSupportedSMSType = (filetype: string) => {
    // Check https://www.twilio.com/docs/sms/accepted-mime-types
    // For Testing https://file-examples.com/index.php/sample-audio-files/sample-wav-download/
    return Utils.supportedSMSTypes.indexOf(filetype) >= 0
  }

  public static getStripeDashboardUrl = (stripeAccountId: string) => {
    return process.env.NODE_ENV == 'development' || process.env.NODE_ENV == 'staging' || process.env.NODE_ENV == 'interop'
      ? `https://dashboard.stripe.com/${stripeAccountId}/test`
      : `https://dashboard.stripe.com/${stripeAccountId}`
  }

  private static supportedSMSTypes = [
    'image/jpeg',
    'image/gif',
    'image/png',
    'image/bmp',
    'text/vcard',
    'text/x-vcard',
    'text/directory',
  ]

  private static supportedMMSTypes = [
    'audio/basic',
    'audio/L24',
    'audio/mp4',
    'audio/mpeg',
    'audio/mp3', // this is not there in list given by twilio but lets see if it goes
    'audio/ogg',
    'audio/vorbis',
    'audio/vnd.rn-realaudio',
    'audio/vnd.wave',
    'audio/3gpp',
    'audio/3gpp2',
    'audio/ac3',
    'audio/vnd.wave',
    'audio/wav', // this is not there in list given by twilio but lets see if it goes
    'audio/webm',
    'audio/amr-nb',
    'audio/amr',
    'video/mpeg',
    'video/mp4',
    'video/quicktime',
    'video/webm',
    'video/3gpp',
    'video/3gpp2',
    'video/3gpp-tt',
    'video/H261',
    'video/H263',
    'video/H263-1998',
    'video/H263-2000',
    'video/H264',
    'image/jpeg',
    'image/gif',
    'image/png',
    'image/bmp',
    'text/vcard',
    'text/x-vcard',
    'text/csv',
    'text/rtf',
    'text/richtext',
    'text/calendar',
    'text/directory',
    'application/pdf',
  ]

  public static randomString(length) {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz'
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const numbers = '0123456789'

    const all = lowercase + uppercase + numbers

    let password = ''

    password += this.pick(password, lowercase, 1)
    password += this.pick(password, uppercase, 1)
    password += this.pick(password, numbers, 1)
    password += this.pick(password, all, length - 3)

    return this.shuffle(password)
  }

  public static pick(exclusions, string, min) {
    let chars = ''
    for (let i = 0; i < min; i++) {
      const character = string.charAt(Math.floor(Math.random() * string.length))
      if (exclusions.indexOf(character) < 0 && chars.indexOf(character) < 0) {
        chars += character
      }
    }
    return chars
  }

  public static shuffle(string) {
    const array = string.split('')
    let tmp,
      current,
      top = array.length

    if (top)
      while (--top) {
        current = Math.floor(Math.random() * (top + 1))
        tmp = array[current]
        array[current] = array[top]
        array[top] = tmp
      }

    return array.join('')
  }

  public static sortByKey(array, key) {
    return array.sort(function (a, b) {
      const x = a[key]
      const y = b[key]
      return x < y ? -1 : x > y ? 1 : 0
    })
  }

  public static hasFirstFixedElements(element: Element, checkChildren = true): boolean {
    const child = element && element.children
    if (element.id !== 'app' && child && child.length > 0) {
      if (window.getComputedStyle(child[0]).position === 'fixed') return true
      if (checkChildren) {
        return Utils.hasFirstFixedElements(child[0], checkChildren)
      }
    }
    return false
  }

  public static setElementDisplay(
    element: HTMLElement | null,
    displayValue: string,
    isMounted: boolean = true,
    attributeClass: string = 'data-previous-display'
  ) {
    try {
      if (element) {
        if (element.hasAttribute(attributeClass)) {
          element.removeAttribute(attributeClass)
          return
        }
        const computedStyle = window.getComputedStyle(element)
        if ((isMounted && element.style.display === displayValue) || computedStyle.display === displayValue)
          element.setAttribute(attributeClass, displayValue)
        else element.style.display = displayValue
      }
    } catch (err) {
      console.warn(err, '')
    }
  }

  public static toggleCustomJsVisibility(
    value: string,
    isMounted: boolean = true,
    attributeClass?: string,
    routeName?: string
  ) {
    // agency custom js breaks email builder drag and drop feature
    // hide that DOM temprorly when user opens email builder

    // do not hide it in workflow list page, location prospect page, and automation workflows page
    if (value === 'none' && (routeName === 'workflows' || routeName === automationRouteNames.workflows || routeName === prospectingRoutes.default)) return

    for (const element of ['customJS', 'freshworks-container', 'beacon-container']) {
      const customJsEl: HTMLElement | null = document.getElementById(element)
      Utils.setElementDisplay(customJsEl, value, isMounted, attributeClass)
    }
    const children = document.body?.children
    if (children && children.length > 0) {
      const childrenArray = Array.prototype.slice.call(children)
      childrenArray.forEach(e => {
        if (
          !e?.id?.includes('pendo') &&
          (window.getComputedStyle(e).position === 'fixed' || Utils.hasFirstFixedElements(e, true))
        ) {
          Utils.setElementDisplay(e, value, true, attributeClass)
        }
      })
    }
  }

  public static convertTimestampToDate(item: any) {
    if (item && item.seconds) return moment(new firebase.firestore.Timestamp(item.seconds, item.nanoseconds).toMillis())
    return item
  }

  public static checkIfFileTypeSupported(type: string) {
    if (!type) return false
    if (type.indexOf('html') > -1 || type.indexOf('php') > -1) return false
    return true
  }

  public static transformToCamelCase(data: any) {
    return mapKeys(data, (value, key) => {
      return camelCase(key)
    })
  }

  public static readableDate(date, tz) {
    return date ? moment(date).tz(tz).format('MMM DD, YYYY') : 'NA'
  }

  public static isInteger(input) {
    const numbers = /^[0-9]+$/
    if (input.toString().match(numbers)) return true
    else return false
  }

  public static convertToSnakeCaseObject(obj) {
    for (const oldName in obj) {
      // convert key to snake case
      const key = snakeCase(oldName)

      // add snake case key in object
      obj[key] = obj[oldName]

      // delete old key
      delete obj[oldName]

      // call function recursively in case of nested object
      if (typeof obj[key] == 'object') {
        obj[key] = Utils.convertToSnakeCaseObject(obj[key])
      }
    }
    return obj
  }


  public static bulkOperations = {
    'bulk-email': i18n.global.t('bulkActions.bulkOperationTypes.bulkEmail'),
    'bulk-sms': i18n.global.t('bulkActions.bulkOperationTypes.bulkSMS'),
    'bulk-export': i18n.global.t('bulkActions.bulkOperationTypes.bulkExport'),
    'bulk-import': i18n.global.t('bulkActions.bulkOperationTypes.bulkImport'),
    'bulk-contact-delete': i18n.global.t('bulkActions.bulkOperationTypes.bulkContactDelete'),
    'bulk-merge-contact': i18n.global.t('bulkActions.bulkOperationTypes.bulkMergeContact'),
    'bulk-tag-add': i18n.global.t('bulkActions.bulkOperationTypes.bulkTagAdd'),
    'bulk-tag-remove': i18n.global.t('bulkActions.bulkOperationTypes.bulkTagRemove'),
    'bulk-email-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkEmailV2'),
    'bulk-whatsapp': i18n.global.t('bulkActions.bulkOperationTypes.bulkWhatsApp'),
    'bulk-tag-add-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkTagAddV2'),
    'bulk-tag-remove-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkTagRemoveV2'),
    'bulk-delete-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkDeleteV2'),
    'restore-bulk-delete-v2': i18n.global.t('bulkActions.bulkOperationTypes.restoreBulkDeleteV2'),
    'restore-bulk-tag-remove-v2': i18n.global.t('bulkActions.bulkOperationTypes.restoreBulkTagRemoveV2'),
    'bulk-review-request': i18n.global.t('bulkActions.bulkOperationTypes.bulkReviewRequest'),
    'bulk-campaign': i18n.global.t('bulkActions.bulkOperationTypes.bulkCampaign'),
    'bulk-workflow': i18n.global.t('bulkActions.bulkOperationTypes.bulkWorkflow'),
    'bulk-ops': i18n.global.t('bulkActions.bulkOperationTypes.bulkOps'),
    'bulk-email-verification': i18n.global.t('bulkActions.bulkOperationTypes.bulkEmailVerification'),
    'bulk-contacts-to-company': i18n.global.t('bulkActions.bulkOperationTypes.bulkContactsToCompany'),
    'bulk-import-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkImport'),
    'bulk-campaign-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkCampaign'),
    'bulk-workflow-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkWorkflow'),
    'bulk-review-request-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkReviewRequest'),
    'bulk-sms-v2': i18n.global.t('bulkActions.bulkOperationTypes.bulkSMS'),
  }
}
